import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, FileText, Folder, Package } from 'lucide-react'
import { useAgentMemory, usePrompts, useSkills, useSkillDetails } from '@/hooks'
import { tryGetManagedPromptName, type AgentYamlConfig } from '@/lib/agentYaml'
import { cn } from '@/lib/utils'
import type { AgentDetail } from '@/types'

interface AgentFilesPreviewProps {
  projectId: string
  agent: AgentDetail | null
  agentName: string
  description: string
  config: AgentYamlConfig | null
  canReadMemory: boolean
}

interface AgentMarkdownHeader {
  name: string
  description: string
  version: string | undefined
}

interface PreviewFile {
  kind: 'file' | 'injected'
  path: string
  content: string
}

interface TreeNode {
  name: string
  path: string
  children: TreeNode[] | null
}

const INDENT_BY_DEPTH = ['pl-4', 'pl-8', 'pl-12', 'pl-16', 'pl-20']

function indentClass(depth: number): string {
  return INDENT_BY_DEPTH[Math.min(depth, INDENT_BY_DEPTH.length) - 1]
}

function buildTree(files: PreviewFile[]): TreeNode[] {
  const roots: TreeNode[] = []
  for (const file of files) {
    const segments = file.path.split('/')
    let level = roots
    segments.forEach((segment, index) => {
      const isLeaf = index === segments.length - 1
      const path = segments.slice(0, index + 1).join('/')
      let node = level.find(candidate => candidate.name === segment && (candidate.children === null) === isLeaf)
      if (!node) {
        node = { name: segment, path, children: isLeaf ? null : [] }
        level.push(node)
      }
      if (!isLeaf) level = node.children!
    })
  }
  return roots
}

function buildAgentMarkdown(header: AgentMarkdownHeader, config: AgentYamlConfig, resolvedPrompt: string): string {
  const lines = ['---', `name: ${header.name}`]
  if (header.description) lines.push(`description: ${header.description.replace(/\s+/g, ' ').trim()}`)
  lines.push(`model: ${config.model}`)
  if (config.effort) lines.push(`effort: ${config.effort}`)
  lines.push(`tools: [${config.tools.join(', ')}]`)
  lines.push(`mcpServers: [${config.mcps.join(', ')}]`)
  lines.push(`memory: ${config.memory === 'project' ? 'agent × project' : 'none'}`)
  if (header.version) lines.push(`version: ${header.version}`)
  lines.push('---', '', resolvedPrompt)
  return lines.join('\n')
}

export function AgentFilesPreview({ projectId, agent, agentName, description, config, canReadMemory }: AgentFilesPreviewProps) {
  const { data: prompts = [], isError: promptsFailed, isPending: promptsPending } = usePrompts(projectId)
  const { data: skills = [], isError: skillsFailed, isPending: skillsPending } = useSkills(projectId)
  const memoryVisible = canReadMemory && config?.memory === 'project'
  const { data: memoryEntries = [] } = useAgentMemory(projectId, memoryVisible ? agent?.id : undefined)
  const [selectedPath, setSelectedPath] = useState<string | null>(null)
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(new Set())

  const selectedSkills = useMemo(() => {
    if (!config) return []
    const byName = new Map(skills.map(skill => [skill.name.toLowerCase(), skill]))
    const seen = new Set<string>()
    return config.skills
      .filter(name => {
        const key = name.toLowerCase()
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      .map(name => ({ name, skill: byName.get(name.toLowerCase()) ?? null }))
  }, [config, skills])

  const skillDetailQueries = useSkillDetails(
    projectId,
    selectedSkills.filter(entry => entry.skill).map(entry => entry.skill!.id)
  )

  const buildFiles = (): PreviewFile[] => {
    if (!config) return []

    const result: PreviewFile[] = []

    let resolvedPrompt = config.systemPrompt
    const internalName = tryGetManagedPromptName(config.systemPrompt)
    if (internalName !== null) {
      const prompt = prompts.find(candidate => candidate.internalName === internalName)
      if (prompt) {
        resolvedPrompt = prompt.content
      } else if (promptsPending) {
        resolvedPrompt = 'Resolving the prompt library…'
      } else if (promptsFailed) {
        resolvedPrompt = "Couldn't load the prompt library to resolve this reference — refresh to try again."
      } else {
        resolvedPrompt = `Managed prompt "${internalName}" was not found in this project — the run will fail to materialize this agent.`
      }
    }

    result.push({
      kind: 'injected',
      path: 'agent system prompt',
      content: buildAgentMarkdown({ name: agentName, description, version: agent?.latestVersion?.version }, config, resolvedPrompt),
    })

    const resolvedEntries = selectedSkills.filter(entry => entry.skill)
    const queryStatesById = new Map(
      resolvedEntries.map((entry, index) => [entry.skill!.id, skillDetailQueries[index]])
    )

    for (const entry of selectedSkills) {
      const query = entry.skill ? queryStatesById.get(entry.skill.id) : null
      let content: string
      if (!entry.skill && skillsPending) {
        content = 'Resolving the skill library…'
      } else if (!entry.skill && skillsFailed) {
        content = "Couldn't load the skill library to resolve this reference — refresh to try again."
      } else if (!entry.skill) {
        content = `Skill "${entry.name}" was not found in this project — the run will fail when this agent is dispatched.`
      } else if (query?.data) {
        content = query.data.content
      } else if (query?.isError) {
        content = 'Couldn\'t load this skill\'s content. Refresh to try again.'
      } else {
        content = 'Loading skill content…'
      }
      result.push({
        kind: 'file',
        path: `${entry.skill?.name ?? entry.name}/SKILL.md`,
        content,
      })
    }

    if (config.memory === 'project') {
      let rendered: string
      if (!canReadMemory) {
        rendered = "This agent's stored memory is only visible to users who can edit agents."
      } else if (memoryEntries.length === 0) {
        rendered = 'No stored memory yet.'
      } else {
        rendered = memoryEntries
          .map(memoryEntry => `- ${memoryEntry.type.toUpperCase()}: ${memoryEntry.content}`)
          .join('\n')
      }
      result.push({ kind: 'injected', path: 'project memory', content: rendered })
    }

    return result
  }

  const files = buildFiles()

  if (!config) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-table-row p-8 text-center text-sm text-destructive" role="alert">
        The YAML is invalid, so the materialized files can't be previewed. Fix it in the Config tab first.
      </div>
    )
  }

  const skillFiles = files.filter(file => file.kind === 'file')
  const injected = files.filter(file => file.kind === 'injected')
  const activeFile = files.find(file => file.path === selectedPath) ?? files[0] ?? null
  const tree = buildTree(skillFiles)

  const toggleFolder = (path: string) => {
    const collapsing = !collapsedFolders.has(path)
    if (collapsing && selectedPath?.startsWith(`${path}/`)) setSelectedPath(null)
    setCollapsedFolders(current => {
      const next = new Set(current)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  return (
    <div className="flex h-[560px] overflow-hidden rounded-2xl border border-border bg-card" data-testid="agent-files-preview">
      <div className="flex w-72 shrink-0 flex-col overflow-y-auto border-r">
        <div className="flex items-center gap-2 px-4 pb-2 pt-3 text-sm font-bold text-foreground">
          <Package className="h-4 w-4 shrink-0 text-amber-700" />
          ~/.claude/skills/
        </div>
        {skillFiles.length > 0 ? (
          <FileTreeNodes
            nodes={tree}
            depth={1}
            activePath={activeFile?.path ?? null}
            collapsedFolders={collapsedFolders}
            onToggleFolder={toggleFolder}
            onSelectFile={setSelectedPath}
          />
        ) : (
          <p className="px-6 pb-1 text-xs italic text-muted-foreground" data-testid="agent-files-no-skills">
            No skills declared, so nothing is written into the container.
          </p>
        )}

        <div className="px-4 pb-1 pt-3 text-xs font-semibold text-muted-foreground">
          Appended to the system prompt
        </div>
        {injected.map(entry => (
          <button
            key={entry.path}
            type="button"
            data-testid={`agent-file-${entry.path}`}
            aria-current={activeFile?.path === entry.path}
            onClick={() => setSelectedPath(entry.path)}
            className={cn(
              'mx-2 flex items-center gap-2 rounded-lg py-2 pl-4 pr-3 text-left text-xs transition-colors',
              activeFile?.path === entry.path
                ? 'bg-primary font-semibold text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted/50',
            )}
          >
            <FileText className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{entry.path}</span>
          </button>
        ))}
      </div>

      <div className="flex min-w-0 flex-1 flex-col bg-[#1e1e1e] text-neutral-200">
        {activeFile ? (
          <>
            <div className="border-b border-neutral-700 px-4 py-2 font-mono text-xs text-neutral-400">
              {activeFile.path}
            </div>
            <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap p-4 font-mono text-xs leading-relaxed">
              {activeFile.content}
            </pre>
          </>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-neutral-400">
            Select a file to preview
          </div>
        )}
      </div>
    </div>
  )
}

interface FileTreeNodesProps {
  nodes: TreeNode[]
  depth: number
  activePath: string | null
  collapsedFolders: Set<string>
  onToggleFolder: (path: string) => void
  onSelectFile: (path: string) => void
}

function FileTreeNodes({ nodes, depth, activePath, collapsedFolders, onToggleFolder, onSelectFile }: FileTreeNodesProps) {
  return (
    <>
      {nodes.map(node => {
        const indent = indentClass(depth)

        if (node.children === null) {
          return (
            <button
              key={node.path}
              type="button"
              data-testid={`agent-file-${node.path}`}
              aria-current={activePath === node.path}
              onClick={() => onSelectFile(node.path)}
              className={cn(
                'mx-2 flex items-center gap-2 rounded-lg py-2 pr-3 text-left font-mono text-xs transition-colors',
                indent,
                activePath === node.path
                  ? 'bg-primary font-semibold text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted/50',
              )}
            >
              <FileText className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{node.name}</span>
            </button>
          )
        }

        const isExpanded = !collapsedFolders.has(node.path)
        return (
          <div key={node.path} className="flex flex-col">
            <button
              type="button"
              onClick={() => onToggleFolder(node.path)}
              aria-expanded={isExpanded}
              data-testid={`agent-folder-${node.path}`}
              className={cn('mx-2 flex items-center gap-1.5 rounded-lg py-2 pr-3 text-left font-mono text-xs font-medium text-foreground transition-colors hover:bg-muted/50', indent)}
            >
              {isExpanded
                ? <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
              <Folder className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{node.name}</span>
            </button>
            {isExpanded && (
              <FileTreeNodes
                nodes={node.children}
                depth={depth + 1}
                activePath={activePath}
                collapsedFolders={collapsedFolders}
                onToggleFolder={onToggleFolder}
                onSelectFile={onSelectFile}
              />
            )}
          </div>
        )
      })}
    </>
  )
}
